/** QuickPlan — Bibliothèque : recherche, catégories, familles ; clic = poser ;
 *  assemblages types (dialogue de paramètres) ; import / export de modèles JSON. */
import { useMemo, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';

export const CAT_LABELS = { alu: 'Profilés alu', meuble: 'Meuble', rack: 'Baies 19"', quincaillerie: 'Quincaillerie', autre: 'Divers', projet: 'Projet' };
const TABS = ['tous', 'alu', 'meuble', 'rack', 'quincaillerie', 'autre', 'projet'];

export default function LibraryPanel() {
  const library = useProjectStore((s) => s.library);
  const project = useProjectStore((s) => s.project);
  const addComponent = useProjectStore((s) => s.addComponent);
  const openDialog = useProjectStore((s) => s.openDialog);
  const publishComponent = useProjectStore((s) => s.publishComponent);
  const deleteLibraryComponent = useProjectStore((s) => s.deleteLibraryComponent);
  const setStatus = useProjectStore((s) => s.setStatus);
  const busy = useProjectStore((s) => !!s.ai.job);
  const [tab, setTab] = useState('tous');
  const [q, setQ] = useState('');
  const fileRef = useRef(null);

  const all = useMemo(() => [
    ...library.components,
    ...(project?.components ?? []).map((c) => ({ ...c, origin: 'projet' })),
  ], [library, project?.components]);

  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const match = (c) => words.every((w) => `${c.name} ${c.family ?? ''} ${c.id} ${c.description ?? ''}`.toLowerCase().includes(w));
  const inTab = (c) => tab === 'tous' || (tab === 'projet' ? c.origin === 'projet' : c.category === tab && c.origin !== 'projet');
  const comps = all.filter((c) => inTab(c) && match(c));
  const templates = library.templates.filter((t) => (tab === 'tous' || t.category === tab) && match(t));

  // Regroupement par famille (ordre d'apparition)
  const families = new Map();
  for (const c of comps) {
    const key = c.origin === 'projet' ? 'Modèles du projet' : c.origin === 'atelier' ? `Atelier — ${c.family ?? CAT_LABELS[c.category]}` : `${CAT_LABELS[c.category]} — ${c.family ?? 'Divers'}`;
    if (!families.has(key)) families.set(key, []);
    families.get(key).push(c);
  }

  const download = (c) => {
    const { origin, ...data } = c;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    a.download = `${c.id}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importFile = async (file) => {
    try {
      const json = JSON.parse(await file.text());
      const saved = await publishComponent(json);
      setStatus(`Modèle « ${saved.name} » ajouté à l'atelier.`);
    } catch (e) {
      setStatus(`⚠ Import impossible : ${e.message}`);
    }
  };

  return (
    <div className="panel-body">
      <input className="search" placeholder="Rechercher (profilé 40, porte, switch…)" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="chips">
        {TABS.map((t) => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>{t === 'tous' ? 'Tous' : CAT_LABELS[t]}</button>
        ))}
      </div>

      {templates.length > 0 && (
        <>
          <h3>Assemblages types</h3>
          {templates.map((t) => (
            <button key={t.id} className="lib-item tpl" disabled={busy} title={t.description} onClick={() => openDialog({ kind: 'template', template: t })}>
              <span className="lib-ico">▣</span>
              <span className="grow">{t.name}</span>
              <span className="lib-cat">{CAT_LABELS[t.category]}</span>
            </button>
          ))}
        </>
      )}

      {[...families.entries()].map(([fam, list]) => (
        <div key={fam}>
          <h3>{fam}</h3>
          {list.map((c) => (
            <div key={`${c.origin}-${c.id}`} className="lib-row">
              <button className="lib-item" disabled={busy} title={`${c.description ?? ''}\n\nClic : poser dans le projet`} onClick={() => addComponent(c.id)}>
                <span className="swatch" style={{ background: c.color }} />
                <span className="grow">{c.name}</span>
              </button>
              <button className="icon" title="Exporter le modèle (JSON)" onClick={() => download(c)}>⤓</button>
              {c.origin === 'projet' && <button className="icon" title="Publier dans la bibliothèque d'atelier (tous les projets)" onClick={() => publishComponent(c).then(() => setStatus(`« ${c.name} » publié dans l'atelier.`)).catch((e) => setStatus(`⚠ ${e.message}`))}>⇪</button>}
              {c.origin === 'atelier' && <button className="icon danger" title="Retirer de l'atelier" onClick={() => window.confirm(`Retirer « ${c.name} » de la bibliothèque d'atelier ?`) && deleteLibraryComponent(c.id)}>✕</button>}
            </div>
          ))}
        </div>
      ))}
      {!comps.length && !templates.length && <div className="empty">Aucun résultat.</div>}

      <div className="lib-footer">
        <button onClick={() => fileRef.current?.click()} title="Importer un modèle de composant (.json) dans la bibliothèque d'atelier">📥 Importer un modèle…</button>
        <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importFile(f); }} />
      </div>
    </div>
  );
}
